/**
 * Gift card codes behind a "unique code per winner" prize.
 *
 * The list comes from the prize pool read (CONTEST_PRIZE_POOL), the one place
 * the owner may see the codes they uploaded. Field names are read loosely, the
 * same way the rest of the details page reads its rows, so a code row works
 * whether the API calls the winner `end_user_id`, `winner_id` or `claimed_by`.
 */

export type GiftCardCodeStatus = 'claimed' | 'not_claimed' | 'expired';

export interface GiftCardCode {
  id: string;
  code: string;
  status: GiftCardCodeStatus;
  /** The winner the code was handed to, or null while it is still in the pool. */
  winnerId: string | null;
  claimedAt: string | null;
  expiresAt: string | null;
}

export interface GiftCardPool {
  prizeId: string;
  total: number;
  claimed: number;
  /** False when the API reported counts but not the codes themselves. */
  listed: boolean;
  codes: GiftCardCode[];
  expiresAt: string | null;
}

/** The prizes out of a CONTEST_PRIZE_POOL answer: `{ prizes }`, or the same under `data`. */
export function readPrizePools(res: any): any[] {
  const body = res?.data ?? res;
  if (Array.isArray(body?.prizes)) return body.prizes;
  if (Array.isArray(body)) return body;
  return [];
}

/** A gift card whose winners each get their own uploaded code. */
export function isUniqueGiftCard(prize: any): boolean {
  return prize?.prize_kind === 'gift_card' && prize?.code_mode === 'unique';
}

function firstArray(...values: any[]): any[] | null {
  for (const value of values) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
  }
  return null;
}

function present(value: any): string | null {
  return value === null || value === undefined || value === '' ? null : String(value);
}

function isPast(date: string | null, now: number): boolean {
  if (!date) return false;
  const time = new Date(date).getTime();
  return Number.isFinite(time) && time < now;
}

// A code that has been claimed stays claimed after its expiry date: the winner
// already has it. Only one still sitting unclaimed can run out.
export function giftCardCodeStatus(row: any, expiresAt: string | null, now = Date.now()): GiftCardCodeStatus {
  const raw = String(row?.status ?? '').toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (raw === 'claimed' || raw === 'redeemed' || raw === 'used') return 'claimed';
  if (raw === 'expired') return 'expired';
  if (raw === 'not_claimed' || raw === 'unclaimed' || raw === 'available') {
    return isPast(expiresAt, now) ? 'expired' : 'not_claimed';
  }

  const claimed = !!(row?.claimed_at || row?.redeemed_at || row?.is_claimed === true
    || row?.claimed === true || present(row?.claimed_by));
  if (claimed) return 'claimed';
  return isPast(expiresAt, now) ? 'expired' : 'not_claimed';
}

export function readGiftCardCode(row: any, index: number, poolExpiresAt: string | null, now = Date.now()): GiftCardCode {
  const expiresAt = present(row?.expires_at) ?? poolExpiresAt;
  return {
    id: String(row?.id ?? index),
    code: String(row?.code ?? row?.voucher_code ?? row?.value ?? '').trim(),
    status: giftCardCodeStatus(row, expiresAt, now),
    winnerId: present(row?.end_user_id ?? row?.winner_id ?? row?.claimed_by ?? row?.participant_id
      ?? row?.user_id ?? row?._end_user?.id),
    claimedAt: present(row?.claimed_at ?? row?.redeemed_at),
    expiresAt,
  };
}

export function readGiftCardPool(pool: any, now = Date.now()): GiftCardPool {
  const expiresAt = present(pool?.expires_at);
  const rows = firstArray(pool?.codes, pool?.code_pool?.codes, pool?.prize_codes, pool?._prize_codes, pool?.code_pool?.items);
  const codes = (rows ?? [])
    .map((row, i) => readGiftCardCode(row, i, expiresAt, now))
    .filter((code) => code.code);
  const claimedInList = codes.filter((code) => code.status === 'claimed').length;

  return {
    prizeId: String(pool?.id ?? ''),
    total: rows ? codes.length : Number(pool?.code_pool?.total) || 0,
    claimed: rows ? claimedInList : Number(pool?.code_pool?.claimed) || 0,
    listed: !!rows,
    codes,
    expiresAt,
  };
}

/** Every id a participant row is known by, as strings — the same ids extractWinners matches on. */
export function participantIds(row: any): string[] {
  return [row?.participant_id, row?.user_id, row?.end_user_id, row?.end_user?.id, row?._end_user?.id]
    .map(present)
    .filter((id): id is string => !!id);
}
